import { useAnalytics, useAnalyticsSearchParams } from "@Light/app/analytics";
import { Feature } from "@Light/app/features";
import { LoggedInApp } from "@Light/components/App";
import { ErrorPage } from "@Light/components/error";
import { WithAccount } from "@Light/components/page/account";
import { Overlay } from "@Light/components/page/Overlay";
import { type LightApi, useLight } from "@Light/services/light";
import type { QueryHookResult } from "@Light/services/queryTypes";
import * as Sentry from "@sentry/react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LoginRoutes } from "./login/LoginRoutes";
import { ShopRoutes } from "./shop/routes";
import {
  WithConfiguration,
  useConfiguration,
  useFeatureFlag,
} from "./WithConfiguration";

function isLoginPath(pathname: string): boolean {
  return pathname.startsWith("/login");
}

function isLoginTokenPath(pathname: string): boolean {
  return pathname.startsWith("/login/token");
}

export const AppRoutes: React.FC = () => {
  useAnalyticsSearchParams();
  return (
    <WithConfiguration>
      <ConfigurationRoutes />
    </WithConfiguration>
  );
};

export const ConfigurationRoutes: React.FC = () => {
  const configuration = useConfiguration();
  const location = useLocation();
  const { useGetAccountQuery } = useLight();
  const account = useGetAccountQuery();

  const hasPlanShopFeatureFlag = useFeatureFlag(
    "CUSTOMER_CLIENT_SHOW_PLAN_SHOP",
  );
  const accountIsFinalized =
    account.isSuccess && account.data.enrollment.is_enrollment_finalized;
  const hasPlanShop = hasPlanShopFeatureFlag && !accountIsFinalized;

  const onLoginPath = isLoginPath(location.pathname);
  const accountPending =
    !account.isSuccess && !account.isError && !account.isFetching;

  if (!onLoginPath && (!configuration || accountPending)) {
    return null;
  }

  const features = configuration?.feature_flags ?? [];

  return (
    <Sentry.ErrorBoundary fallback={ErrorPage}>
      <Feature.Provider features={features}>
        <Routes>
          {hasPlanShop && <Route path="/shop/*" element={<ShopRoutes />} />}
          <Route
            path="/*"
            element={<ConfigurationRoutesB1 account={account} />}
          />
        </Routes>
      </Feature.Provider>
    </Sentry.ErrorBoundary>
  );
};

export type ConfigurationRoutesB1Props = {
  account: QueryHookResult<
    LightApi["endpoints"]["getAccount"]["Types"]["QueryDefinition"]
  >;
};

export const ConfigurationRoutesB1: React.FC<ConfigurationRoutesB1Props> = ({
  account,
}) => {
  const { planUUID } = useAnalytics();
  const location = useLocation();
  const onLoginTokenPage = isLoginTokenPath(location.pathname);

  if (account.isError || onLoginTokenPage) {
    return (
      <Routes>
        <Route path="/login/*" element={<LoginRoutes />} />
        {account.isError && (
          <Route path="/*" element={<Navigate to="/login" />} />
        )}
      </Routes>
    );
  }

  return (
    <Overlay>
      <WithAccount>
        <Routes>
          <Route path="/*" element={<LoggedInApp planUUID={planUUID} />} />
        </Routes>
      </WithAccount>
    </Overlay>
  );
};
